import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import MicrosoftAzureCloudGrantRolesSteps from "../MicrosoftAzureCloud/MicrosoftAzureCloudGrantRolesSteps";
import MicrosoftOffice365GenerateKeyStep from "../MicrosoftOffice365/MicrosoftOffice365GenerateKeyStep";
import microsoft_azure10 from "./images/microsoft_azure10.png";
import microsoft_azure11 from "./images/microsoft_azure11.png";
import microsoft_azure12 from "./images/microsoft_azure12.png";
import microsoft_azure20 from "./images/microsoft_azure20.png";
import microsoft_azure4 from "./images/microsoft_azure4.png";
import microsoft_azure5 from "./images/microsoft_azure5.png";
import microsoft_azure7 from "./images/microsoft_azure7.png";
import microsoft_tap_permissions from "./images/tap_permissions.png";
import MicrosoftAzureApplicationRoles from "./MicrosoftAzureApplicationRoles";

const MicrosoftAzureRegisteredIntegration = ({
  integrationName,
  registeredMicrosoftIntegrations,
  permissions,
  windowsDefenderAtpPermissions,
  windows365OfficePermissions,
  setLoading,
  guideMode = false,
}) => {
  const colors = useColors();
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerMicrosoftIntegration } = useAPI();
  const [isGovTenant, setIsGovTenant] = useState(false);
  const queryClient = useQueryClient();
  const { staticIPs: allowlistIps } = useStaticIPs();

  const integrationDisplayNames = {
    intune: "Microsoft Intune",
    microsoft_defender_endpoint: "Microsoft Defender for Endpoint",
    microsoft_entra_id: "Microsoft Entra ID",
    microsoft_office_365: "Microsoft Office 365",
    azure: "Microsoft Azure Cloud",
  };

  const integrationDisplayName = integrationDisplayNames[integrationName];

  const handleConnectClick = () => {
    setLoading(true);
    registerMicrosoftIntegration(
      integrationName,
      registeredMicrosoftIntegrations,
      isGovTenant
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integrationDisplayName} integration successful`);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      {/* BUTTONS TO CONTROL EXPANSION */}
      <Box mb="20px">
        <Grid container spacing={2} direction={"row"}>
          {guideMode && <IntegrationGuideAlert />}

          <Grid item>
            <SecondaryButton onClick={handleExpandAll}>
              Expand All Screenshots
            </SecondaryButton>
          </Grid>
          <Grid item>
            <SecondaryButton onClick={handleCollapseAll}>
              Collapse All Screenshots
            </SecondaryButton>
          </Grid>
          <Grid item>
            <SecondaryButton onClick={() => setShowStaticIPsModal(true)}>
              View Breeze Static IPs
            </SecondaryButton>
          </Grid>
        </Grid>
      </Box>
      {/* Integration Status */}
      <Box mb={4}>
        <Alert severity="info">
          <AlertTitle>
            You already have a registered Microsoft integration
          </AlertTitle>
          <Typography variant="body1" gutterBottom>
            Use your existing credentials and add the permissions below.
          </Typography>
        </Alert>
      </Box>
      {/* SETUP INSTRUCTIONS */}
      <GuidelineStepByStep>
        <GuidelineStep title="Login to the Entra Portal">
          <Box style={{ display: "flex" }}>
            <PrimaryButton
              onClick={() => {
                window.open("https://entra.microsoft.com/", "_blank");
              }}
            >
              Sign In
            </PrimaryButton>
          </Box>
        </GuidelineStep>
        <GuidelineStep title="Assign new API permissions">
          <Typography variant="body1">
            Navigate to the{" "}
            <strong> Applications {"->"} App registrations</strong>
          </Typography>
          <Typography variant="body1">
            Click on <strong> View all applications in the directory </strong>{" "}
            button.
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure10,
                altext: "Navigate to all applications page",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Typography variant="body1">
            Select the application you have created for <b>Breeze</b>.
          </Typography>
          <Typography variant="body1">
            Navigate to the <strong>API permissions </strong> tab and click
            <strong> Add a permission </strong>
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure4,
                altext: "Navigate to the API permissions tab",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Typography variant="body1">
            Select{" "}
            <strong> Microsoft Graph {"->"} Application permissions </strong> ,
            and grant (if not already granted)
          </Typography>
          <Typography variant="body1">
            {permissions.length === 1 ? "this" : "these"}
            <strong> {permissions.length} permissions:</strong>
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure5,
                altext: "Select Microsoft Graph -> Application permissions",
              },
            ]}
            onExpanded={addToExpandedList}
          />
          <Box
            sx={{
              width: "700px",
              backgroundColor: colors.grey_200,
              padding: "16px",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="subtitle2"
              color={colors.primary_darker}
              gutterBottom
            >
              The list of required permissions
            </Typography>
            <Typography variant="body2" gutterBottom>
              Check the box for each completed permission to track the progress
            </Typography>
            {permissions.map((permission, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <Checkbox />
                <Typography>{permission}</Typography>
              </Box>
            ))}
          </Box>
        </GuidelineStep>
        {integrationName === "microsoft_defender_endpoint" && (
          <GuidelineStep title="Configure WindowsDefenderATP">
            <Typography variant="body1">
              Select <strong>APIs my organization uses</strong> and search for
              <strong> WindowsDefenderATP </strong>
            </Typography>
            <ScreenshotAccordion
              images={[
                {
                  file: microsoft_azure11,
                  altext: "Grant WindowsDefenderATP permissions",
                },
              ]}
              onExpanded={addToExpandedList}
            />
            <Typography variant="body1">
              Then, add the following{" "}
              <strong>
                {windowsDefenderAtpPermissions?.length} permissions:
              </strong>
            </Typography>
            <ScreenshotAccordion
              images={[
                {
                  file: microsoft_tap_permissions,
                  altext: "Grant WindowsDefenderATP permissions",
                },
              ]}
              onExpanded={addToExpandedList}
            />
            <Box
              sx={{
                width: "700px",
                backgroundColor: colors.grey_200,
                padding: "16px",
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="subtitle2"
                color={colors.primary_darker}
                gutterBottom
              >
                The list of required permissions
              </Typography>
              <Typography variant="body2" gutterBottom>
                Check the box for each completed permission to track the
                progress
              </Typography>
              {windowsDefenderAtpPermissions?.map((permission, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <Checkbox />
                  <Typography>{permission}</Typography>
                </Box>
              ))}
            </Box>
          </GuidelineStep>
        )}
        <GuidelineStep title="Assign new API permissions">
          {integrationName === "microsoft_office_365" && (
            <>
              <Typography variant="body1">
                Select <strong>APIs my organization uses</strong> and search for
                <strong> Office 365 Exchange Online </strong>
              </Typography>
              <ScreenshotAccordion
                images={[
                  {
                    file: microsoft_azure12,
                    altext: "Grant Office 365 Exchange Online permissions",
                  },
                ]}
                onExpanded={addToExpandedList}
              />
              <Typography variant="body1">
                Add the following{" "}
                <strong>
                  {windows365OfficePermissions?.length} permissions:
                </strong>
              </Typography>
              <Box
                sx={{
                  width: "700px",
                  backgroundColor: colors.grey_200,
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color={colors.primary_darker}
                  gutterBottom
                >
                  The list of required permissions
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Check the box for each completed permission to track the
                  progress
                </Typography>
                {windows365OfficePermissions?.map((permission, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Checkbox />
                    <Typography>{permission}</Typography>
                  </Box>
                ))}
              </Box>
              <ScreenshotAccordion
                images={[
                  {
                    file: microsoft_azure20,
                    altext: "Grant Office 365 Exchange Online permissions",
                  },
                ]}
                onExpanded={addToExpandedList}
              />
            </>
          )}
          <Typography variant="body1">
            When done adding permissions, please review the complete list of
            permissions.
          </Typography>
          <Typography variant="body1">
            Then click on the <strong>"Grant admin consent for"</strong> button.
          </Typography>
          <Typography variant="body1">
            Press <strong>Yes</strong> to confirm.
          </Typography>
          <ScreenshotAccordion
            images={[
              {
                file: microsoft_azure7,
                altext: "Click on Grant admin consent",
              },
            ]}
            onExpanded={addToExpandedList}
          />
        </GuidelineStep>
        {integrationName === "azure" && (
          <MicrosoftAzureCloudGrantRolesSteps
            addToExpandedList={addToExpandedList}
          />
        )}
        {integrationName === "microsoft_office_365" && (
          <MicrosoftOffice365GenerateKeyStep
            addToExpandedList={addToExpandedList}
          />
        )}
        {(integrationName === "microsoft_office_365" ||
          integrationName === "microsoft_entra_id") && (
          <MicrosoftAzureApplicationRoles
            addToExpandedList={addToExpandedList}
            integrationName={integrationName}
          />
        )}
        <GuidelineStep title="Add Breeze to the allowlist Network IP - Optional">
          <GuidelineInstruction
            text={
              <span>
                Add the following IP addresses to the allow list (e.g., CA
                policies, Azure KeyVault FW) to enable in-depth analysis:{" "}
                <strong>
                  {allowlistIps.map((ip, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                      }}
                    >
                      <span>{ip}</span>
                      <CustomCopyButton
                        copyTitle={"IP Address"}
                        copyText={ip}
                      />
                    </Box>
                  ))}
                </strong>
              </span>
            }
          />
        </GuidelineStep>
        <GuidelineStep title="Connect the Integration">
          <Typography variant="body1">
            Click connect to integrate your {integrationDisplayName} with
            Breeze.
          </Typography>
          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isGovTenant}
                  onChange={(e) => setIsGovTenant(e.target.checked)}
                  name="isGovTenant"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    Government Tenant (GCC)
                  </Typography>
                  <Typography variant="caption" color={colors.text_secondary}>
                    Check this box if you are connecting to a US Government
                    (GCC) tenant
                  </Typography>
                </Box>
              }
            />
          </Box>
          <Box display="flex" justifyContent="center">
            <PrimaryButton
              type="submit"
              onClick={handleConnectClick}
              disabled={guideMode}
            >
              Connect
            </PrimaryButton>
          </Box>
        </GuidelineStep>
      </GuidelineStepByStep>
      <BreezeStaticIPsModal
        open={showStaticIPsModal}
        onClose={() => setShowStaticIPsModal(false)}
      />
    </div>
  );
};

export default MicrosoftAzureRegisteredIntegration;
